import React, { useRef, useEffect, useCallback } from 'react'

function formatTime(time) {
  if (!Number.isFinite(time) || time < 0) return '0:00'
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
}

function timeFromPointer(clientX, barEl, duration) {
  if (!barEl || !Number.isFinite(duration) || duration <= 0) return 0
  const rect = barEl.getBoundingClientRect()
  if (rect.width <= 0) return 0
  const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
  return pct * duration
}

function applyScrubVisuals(fillEl, currentLabelEl, time, duration) {
  const pct = duration > 0 ? (time / duration) * 100 : 0
  if (fillEl) fillEl.style.width = `${pct}%`
  if (currentLabelEl) currentLabelEl.textContent = formatTime(time)
}

export default function ProgressBar({
  currentTime,
  duration,
  onSeek,
  onDragChange,
}) {
  const barRef = useRef(null)
  const fillRef = useRef(null)
  const currentTimeLabelRef = useRef(null)
  const isDraggingRef = useRef(false)
  const didDragRef = useRef(false)
  const pointerIdRef = useRef(null)

  const setDragging = useCallback(
    (dragging) => {
      isDraggingRef.current = dragging
      onDragChange?.(dragging)
      if (barRef.current) {
        barRef.current.classList.toggle('is-dragging', dragging)
      }
    },
    [onDragChange],
  )

  const endDrag = useCallback(
    (e) => {
      if (!isDraggingRef.current) return

      const bar = barRef.current
      const moved = didDragRef.current

      if (bar && e && Number.isFinite(duration) && duration > 0) {
        const boundedTime = timeFromPointer(e.clientX, bar, duration)
        onSeek?.(boundedTime)
      }

      if (fillRef.current) fillRef.current.style.width = ''

      setDragging(false)
      didDragRef.current = false
      pointerIdRef.current = null

      if (bar && e?.pointerId != null) {
        try {
          bar.releasePointerCapture(e.pointerId)
        } catch {
          /* already released */
        }
      }

      if (moved) {
        e?.preventDefault()
        e?.stopPropagation()
      }
    },
    [duration, onSeek, setDragging],
  )

  const handlePointerDown = useCallback(
    (e) => {
      if (!Number.isFinite(duration) || duration <= 0) return
      if (e.button !== 0 && e.pointerType === 'mouse') return

      const bar = barRef.current
      if (!bar) return

      e.preventDefault()
      didDragRef.current = false
      pointerIdRef.current = e.pointerId
      setDragging(true)

      try {
        bar.setPointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }

      const t = timeFromPointer(e.clientX, bar, duration)
      applyScrubVisuals(fillRef.current, currentTimeLabelRef.current, t, duration)
    },
    [duration, setDragging],
  )

  const handlePointerMove = useCallback(
    (e) => {
      if (!isDraggingRef.current) return
      if (
        pointerIdRef.current != null &&
        e.pointerId !== pointerIdRef.current
      ) {
        return
      }

      const bar = barRef.current
      if (!bar || !Number.isFinite(duration) || duration <= 0) return

      e.preventDefault()
      didDragRef.current = true

      const t = timeFromPointer(e.clientX, bar, duration)
      applyScrubVisuals(fillRef.current, currentTimeLabelRef.current, t, duration)
    },
    [duration],
  )

  const handlePointerUp = useCallback(
    (e) => {
      endDrag(e)
    },
    [endDrag],
  )

  const handlePointerCancel = useCallback(
    (e) => {
      if (!isDraggingRef.current) return
      const bar = barRef.current
      if (fillRef.current) fillRef.current.style.width = ''
      if (bar && e?.pointerId != null) {
        try {
          bar.releasePointerCapture(e.pointerId)
        } catch {
          /* already released */
        }
      }
      setDragging(false)
      didDragRef.current = false
      pointerIdRef.current = null
    },
    [setDragging],
  )

  useEffect(() => {
    if (isDraggingRef.current) return
    if (currentTimeLabelRef.current) {
      currentTimeLabelRef.current.textContent = formatTime(currentTime)
    }
  }, [currentTime])

  const fillWidth =
    duration > 0 ? `${(currentTime / duration) * 100}%` : '0%'

  return (
    <div className="progress-container">
      <span className="time" ref={currentTimeLabelRef}>
        {formatTime(currentTime)}
      </span>
      <div
        ref={barRef}
        className="progress-bar"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <div
          ref={fillRef}
          className="progress"
          style={{ width: fillWidth }}
        />
      </div>
      <span className="time">{formatTime(duration)}</span>
    </div>
  )
}
